/**
 * @file pages/Login.js
 * @description Page de connexion
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/moments';

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur de connexion. Vérifiez vos identifiants.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 24px 60px;
          background: linear-gradient(135deg, #1F2A44 0%, #141d30 100%);
          position: relative;
        }
        .auth-page::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 0%, rgba(169, 134, 103, 0.12), transparent 60%);
        }
        .auth-card {
          position: relative;
          width: 100%;
          max-width: 460px;
          background: rgba(31, 42, 68, 0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 24px;
          padding: 48px 40px;
          animation: fadeInUp 0.5s ease;
        }
        .auth-logo {
          text-align: center;
          margin-bottom: 8px;
        }
        .auth-logo-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, rgba(169, 134, 103, 0.2), rgba(169, 134, 103, 0.05));
          border: 1px solid rgba(169, 134, 103, 0.3);
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          margin-bottom: 20px;
        }
        .auth-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.2rem;
          color: #FFFFFF;
          text-align: center;
          margin-bottom: 8px;
        }
        .auth-subtitle {
          color: rgba(240, 234, 226, 0.55);
          text-align: center;
          font-size: 0.95rem;
          margin-bottom: 36px;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgba(240, 234, 226, 0.7);
          margin-bottom: 8px;
          letter-spacing: 0.3px;
        }
        .form-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 10px;
          padding: 14px 16px;
          color: #FFFFFF;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          outline: none;
        }
        .form-input:focus {
          border-color: #A98667;
          background: rgba(169, 134, 103, 0.08);
          box-shadow: 0 0 0 3px rgba(169, 134, 103, 0.12);
        }
        .form-input::placeholder {
          color: rgba(240, 234, 226, 0.3);
        }
        .auth-error {
          background: rgba(231, 76, 60, 0.12);
          border: 1px solid rgba(231, 76, 60, 0.3);
          border-radius: 10px;
          padding: 12px 16px;
          color: #e74c3c;
          font-size: 0.9rem;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .auth-submit {
          width: 100%;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          color: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
          letter-spacing: 0.3px;
        }
        .auth-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(169, 134, 103, 0.4);
        }
        .auth-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .auth-footer {
          text-align: center;
          margin-top: 28px;
          font-size: 0.9rem;
          color: rgba(240, 234, 226, 0.55);
        }
        .auth-footer a {
          color: #A98667;
          font-weight: 600;
          transition: color 0.2s;
        }
        .auth-footer a:hover {
          color: #c4a07d;
        }
        .auth-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 24px 0;
          color: rgba(240, 234, 226, 0.3);
          font-size: 0.85rem;
        }
        .auth-divider::before, .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(169, 134, 103, 0.15);
        }
        @media (max-width: 500px) {
          .auth-card { padding: 36px 24px; }
        }
      `}</style>

      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <div className="auth-logo-icon">✦</div>
          </div>
          <h1 className="auth-title">Bon retour</h1>
          <p className="auth-subtitle">Connectez-vous pour accéder à votre espace d'étude</p>

          {error && (
            <div className="auth-error">
              ⚠ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="email">Adresse email</label>
              <input
                id="email"
                type="email"
                name="email"
                className="form-input"
                placeholder="votre@email.com"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="password">Mot de passe</label>
              <input
                id="password"
                type="password"
                name="password"
                className="form-input"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </form>

          <div className="auth-divider">ou</div>

          <div className="auth-footer">
            Pas encore de compte ?{' '}
            <Link to="/register">Créer un compte</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
